import { normalizeAcMenuPath, toAcMenuRoutePath } from "../acMenu/filterAcMenus";
import { pathAuthWhiteList, RIMS_SESSION_STG_CONSTANTS } from "../constants";
import { STATIC_MODULE_ROUTE_PATHS } from "../constants/acMenu";
import type { AcMenuItem } from "../types/acMenu";

/** Static module roots that restore to the first two path segments on tenant switch. */
const TWO_LEVEL_MODULE_ROOTS = new Set([
  "/configuration",
  "/bpmn",
  "/user-access-management",
]);

function applyModuleHomeDepth(normalized: string, staticRoot: string): string {
  if (!TWO_LEVEL_MODULE_ROOTS.has(staticRoot)) {
    return staticRoot;
  }
  const segments = normalized.split("/").filter(Boolean);
  if (segments.length <= 2) {
    return normalized;
  }
  return `/${segments.slice(0, 2).join("/")}`;
}

/** Map any AC route to its module home (SideMenu / iframe entry). */
export function normalizeAcModuleHomePath(
  pathname: string,
  matchedAcMenus: AcMenuItem[] = []
): string {
  const normalized = normalizeAcMenuPath(pathname);
  if (!normalized || normalized === "/") {
    return normalized;
  }

  let bestStatic = "";
  for (const staticPath of Array.from(STATIC_MODULE_ROUTE_PATHS)) {
    if (
      normalized === staticPath ||
      normalized.startsWith(`${staticPath}/`)
    ) {
      if (staticPath.length > bestStatic.length) {
        bestStatic = staticPath;
      }
    }
  }
  if (bestStatic) {
    return applyModuleHomeDepth(normalized, bestStatic);
  }

  if (pathAuthWhiteList.includes(normalized)) {
    return normalized;
  }

  for (const item of matchedAcMenus) {
    const routePath = toAcMenuRoutePath(item.name);
    if (!routePath) continue;
    if (normalized === routePath || normalized.startsWith(`${routePath}/`)) {
      return routePath;
    }
  }

  return normalized;
}

export function persistLastAcModulePath(
  pathname: string,
  matchedAcMenus: AcMenuItem[] = []
): void {
  const home = normalizeAcModuleHomePath(pathname, matchedAcMenus);
  if (!home) return;
  sessionStorage.setItem(RIMS_SESSION_STG_CONSTANTS.LAST_AC_MODULE_PATH, home);
}

export function getLastAcModulePath(): string {
  return (
    sessionStorage.getItem(RIMS_SESSION_STG_CONSTANTS.LAST_AC_MODULE_PATH) || ""
  );
}

/**
 * Resolve where to land after tenant switch.
 * AC does not refetch privileges on tenant switch (menu/authList are login-time),
 * so this does not gate on authList — only session path → current path → /reports.
 */
export function resolveTenantSwitchReturnPath(options: {
  currentPathname: string;
  matchedAcMenus?: AcMenuItem[];
}): string {
  const { currentPathname, matchedAcMenus = [] } = options;
  const seen = new Set<string>();
  const candidates = [
    getLastAcModulePath(),
    normalizeAcModuleHomePath(currentPathname, matchedAcMenus),
  ].filter((path) => {
    if (!path || path === "/" || seen.has(path)) return false;
    seen.add(path);
    return true;
  });

  return candidates[0] || "/reports";
}
